import React, { useEffect, useState } from "react";
import styles from './ClinicalSite.module.scss';
import PageTitle from "../../../components/pageTitle/PageTitle";
import Search from "../../../components/search/Search";
import Filter from "../../../components/filter/Filter";
import Button from "../../../components/button/Button";
import ClinicalSiteCard from "../../../components/clinicalSiteCard/ClinicalSiteCard";
import axiosClient from "../../../api/axiosClient";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import LoadMore from "../../../components/loadMore/loadMore";
import { useToasts } from "../../../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../../../utils/stringUtils";

const ClinicalSite = () => {
  const limit = 20;
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All Clinical Sites');
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchSites = async () => {
      if(page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadMoreLoading(true);
      }

      const params = {
        limit: limit,
        page: page,
        search: search,
      };

      if (selectedFilter && selectedFilter !== 'All Clinical Sites') {
        params.status = selectedFilter === 'Active';
      }

      await axiosClient.get(`clinical-sites/list`, {
        params: params
      })
      .then((response) => {
        if(response.data?.clinicalSites) {
          if(page === 1) {
            setSites([]);
          }
          setSites((prevData) => {
            return [...prevData, ...response.data.clinicalSites.map((site) => ({
              id: site.id,
              name: site.name,
              address: site.address ?? '', // Handle potential null address
              isActive: site.status ?? false, // API returns 'status' as boolean
              siteId: site.id, // Using id as siteId for now
              phone: site.mobileNumber ? formatPhoneNumber(site.mobileNumber) : '', // Format phone number
              specialtiesCount: site.specialities ? site.specialities.length : 0, // Count specialities
              agreementStatus: site.agreementStatus
            }))];
          });
          setTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadMoreLoading(false);
      });
    };

    fetchSites();
  }, [page, search, selectedFilter, showToast]);

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const loadMore = async () => {
    setPage(page + 1);
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle
          label="Clinical Site"
        />
        <div className={styles.searchWrapper}>
          <Search 
            onSubmit={handleSearch}
          />
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={[
                {
                  label: 'All Clinical Sites',
                  value: 'All Clinical Sites',
                },
                {
                  label: 'Active',
                  value: 'Active',
                },
                {
                  label: 'Inactive',
                  value: 'Inactive',
                }
              ]}
              selected={selectedFilter}
              onChange={(filter) => {
                setSelectedFilter(filter);
                setPage(1);
              }}
            />
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              label="Add New Clinical Site"
              size="medium"
              onClick={() => navigate('/clinical-sites/create')}
            />
          </div>
        </div>
        
        {sites.length > 0 ? <div className={styles.cardList}>
          {sites.map((site, index) => {
            return (
              <div className={styles.cardItem} key={`site-${index}`}>
                <ClinicalSiteCard
                  name={site.name}
                  address={site.address}
                  status={site.isActive ? 'Active' : 'Inactive'}
                  detailItems={[
                    { label: 'Clinical Site ID', value: site.siteId },
                    { label: 'Phone', value: site.phone },
                    { label: 'Specialties', value: site.specialtiesCount },
                    { label: 'Affiliation Agreement', value: site.agreementStatus }
                  ]}
                  onClick={() => navigate(`/clinical-sites/edit/${site.id}`)} 
                />
              </div>
            );
          })}
          {(isLoadMoreLoading || page < totalPages) 
            && <LoadMore isLoading={isLoadMoreLoading} onClick={loadMore} />}
        </div> : !isLoading && <div className={styles.empty}>
          <p>No clinical sites found.</p>
        </div>}
      </div>
    </>
  );
};

export default ClinicalSite;
